import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingCart, Plus, Trash2, Wallet, X, Camera, Loader2, Sparkles } from "lucide-react";
import InlinePriceHint from "../InlinePriceHint";
import ConditioningSuggester from "../ConditioningSuggester";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🆕 Fév 2026 — Scan de facture par IA (Gemini 3.1 Pro).
// Redimensionne l'image ≤ 1600 px et encode en base64 avant envoi au backend.
async function fileToScaledBase64(file, maxDim = 1600, quality = 0.85) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const ratio = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl.split(",")[1]); // base64 pur (sans "data:image/...")
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const InvoiceScanner = ({ onScanned, disabled }) => {
  const [loading, setLoading] = React.useState(false);
  const fileRef = React.useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Merci de choisir une image (JPEG, PNG, WebP).");
      return;
    }
    setLoading(true);
    const toastId = toast.loading("Analyse de la facture par l'IA…");
    try {
      const image_base64 = await fileToScaledBase64(file);
      const res = await axios.post(
        `${API}/receipt-scan/extract`,
        {
          image_base64,
          mime_type: "image/jpeg",
          auto_create_expense: false,
          receipt_type: "auto",
        },
        { timeout: 60000 }
      );
      const extracted = res?.data?.extracted;
      if (!extracted || !Array.isArray(extracted.items) || extracted.items.length === 0) {
        toast.error("Aucun article détecté sur la facture.", { id: toastId });
        return;
      }
      onScanned(extracted);
      toast.success(
        `Facture lue — ${extracted.items.length} article${extracted.items.length > 1 ? "s" : ""} extrait${extracted.items.length > 1 ? "s" : ""}${extracted.confidence ? ` (fiabilité : ${extracted.confidence})` : ""}`,
        { id: toastId }
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Erreur lors du scan";
      toast.error(String(msg), { id: toastId });
    } finally {
      setLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/20 border border-cyan-500/40 rounded-lg p-3">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 border border-cyan-500/40 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-cyan-300" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-cyan-200 flex items-center gap-1.5">
            Scanner une facture (IA)
            <span className="text-[9.5px] bg-cyan-500/30 text-cyan-100 px-1.5 py-0.5 rounded-full uppercase tracking-wide font-bold">Gemini</span>
          </p>
          <p className="text-[11px] text-cyan-100/70 mt-0.5">
            Prenez ou choisissez une photo — l&apos;IA remplit automatiquement le fournisseur et les articles. Vous relisez et validez.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="scan-invoice-camera-input"
            />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="scan-invoice-gallery"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="scan-invoice-gallery-input"
            />
            <Button
              type="button"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={disabled || loading}
              className="bg-cyan-600 hover:bg-cyan-700 text-white h-8 text-xs font-bold"
              data-testid="scan-invoice-camera-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Camera className="w-4 h-4 mr-1" strokeWidth={2.4} />}
              {loading ? "Analyse en cours…" : "Prendre / choisir une photo"}
            </Button>
            <label
              htmlFor="scan-invoice-gallery"
              className={`inline-flex items-center gap-1 h-8 px-3 rounded-md border text-xs font-semibold cursor-pointer ${loading || disabled ? "opacity-50 pointer-events-none" : ""} border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10`}
              data-testid="scan-invoice-gallery-btn"
            >
              Galerie
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

const ExpenseModal = ({
  open,
  onOpenChange,
  // Data
  editingExpense,
  expenseForm,
  setExpenseForm,
  commonItems,
  setCommonItems,
  commonNewItem,
  setCommonNewItem,
  availableAccounts,
  expenseSubmitLoading,
  // Constants
  EXPENSE_TYPES,
  DESTINATIONS,
  PREDEFINED_PAYMENTS,
  PAYMENT_GROUPS,
  // Helpers
  formatPrice,
  getCommonTotal,
  addCommonItem,
  removeCommonItem,
  createExpense,
  updateExpense,
  onClose,
}) => {
  const handleOpenChange = (isOpen) => {
    if (!isOpen && commonItems.length > 0 && !editingExpense) {
      if (!window.confirm("Articles non enregistrés. Fermer quand même ?")) return;
    }
    if (!isOpen) {
      onOpenChange(false);
      onClose();
      setCommonItems([]);
      setCommonNewItem({ category: "cuisine", description: "", quantity: 0, unit_price: 0 });
      setExpenseForm({
        category: "cuisine",
        description: "",
        quantity: 1,
        unit_price: 0,
        amount: 0,
        supplier: "",
        planned_date: format(new Date(), "yyyy-MM-dd"),
        receipt_image: null,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="expense-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-purple-400 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {editingExpense ? "Modifier la demande" : "Nouveaux achats communs"}
            {commonItems.length > 0 && !editingExpense && (
              <Badge className="bg-purple-500/30 text-purple-200 ml-2">
                {commonItems.length} article(s) • {formatPrice(getCommonTotal())} F
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            Ajoutez un ou plusieurs articles (catégories différentes possibles). Le fournisseur, la date et le reçu sont communs.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {editingExpense ? (
            /* --- EDIT MODE (admin) --- */
            <>
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <Label className="text-slate-300 text-xs uppercase tracking-wide mb-2 block">Type d'opération (Admin)</Label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPENSE_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setExpenseForm((p) => ({ ...p, expense_type: t.value }))}
                      data-testid={`edit-expense-type-${t.value}-btn`}
                      className={`px-3 py-2 rounded-lg border text-left transition-colors ${
                        (expenseForm.expense_type || "achat") === t.value
                          ? t.value === "paiement"
                            ? "bg-rose-500/20 border-rose-500/60 text-rose-100"
                            : "bg-emerald-500/20 border-emerald-500/60 text-emerald-100"
                          : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <span className="text-base mr-1">{t.icon}</span>
                      <span className="font-bold">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Catégorie *</Label>
                  <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v })}>
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="cuisine">🍳 Cuisine</SelectItem>
                      <SelectItem value="bar">🍹 Bar</SelectItem>
                      <SelectItem value="paiement">💳 Paiement</SelectItem>
                      <SelectItem value="autres">📦 Autres</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">Destination</Label>
                  <Select
                    value={expenseForm.destination || "cuisine"}
                    onValueChange={(v) => setExpenseForm({ ...expenseForm, destination: v })}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="edit-expense-destination-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {DESTINATIONS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.icon} {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Description / Libellé *</Label>
                <Textarea
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  placeholder="Ex: Viande de boeuf, légumes frais..."
                  className="bg-slate-700/50 border-slate-600 text-white"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Quantité *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="any"
                    value={expenseForm.quantity}
                    onChange={(e) => setExpenseForm({ ...expenseForm, quantity: parseFloat(e.target.value.replace(",", ".")) || 1 })}
                    placeholder="1"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Prix unitaire (FCFA) *</Label>
                  <Input
                    type="number"
                    value={expenseForm.unit_price}
                    onChange={(e) => setExpenseForm({ ...expenseForm, unit_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>
              </div>

              <div className="bg-indigo-900/30 rounded-lg p-3 border border-indigo-500/30">
                <div className="flex justify-between items-center">
                  <span className="text-slate-300">Total calculé:</span>
                  <span className="text-xl font-bold text-indigo-400">
                    {formatPrice(expenseForm.quantity * expenseForm.unit_price)} F
                  </span>
                </div>
              </div>
            </>
          ) : (
            /* --- CREATE MODE (multi-items + Achat / Paiement) --- */
            <>
              {/* 🆕 Fév 2026 — Scan de facture par IA (extraction auto des articles). */}
              {expenseForm.expense_type !== "paiement" && (
                <InvoiceScanner
                  disabled={expenseSubmitLoading}
                  onScanned={(extracted) => {
                    // Pré-remplit fournisseur si vide
                    setExpenseForm((p) => ({
                      ...p,
                      supplier: p.supplier || extracted.supplier || "",
                      description: p.description || `Scan facture — ${extracted.supplier || "Fournisseur"}`,
                    }));
                    // Ajoute chaque item extrait dans commonItems (avec catégorie par défaut = destination courante).
                    const defaultCategory = (expenseForm.destination === "bar" || expenseForm.category === "bar") ? "bar" : "cuisine";
                    const now = Date.now();
                    const newItems = (extracted.items || []).map((it, idx) => ({
                      id: `scan-${now}-${idx}`,
                      category: defaultCategory,
                      description: it.description || "",
                      quantity: Number(it.quantity) || 1,
                      unit_price: Number(it.unit_price) || 0,
                      amount: Number(it.amount) || (Number(it.quantity) || 1) * (Number(it.unit_price) || 0),
                      expense_type: "achat",
                      passer_en_stock: null,
                    }));
                    setCommonItems((prev) => [...(prev || []), ...newItems]);
                  }}
                />
              )}

              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                <Label className="text-slate-300 text-xs uppercase tracking-wide mb-2 block">Type d'opération</Label>
                <div className="grid grid-cols-2 gap-2">
                  {EXPENSE_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => {
                        setExpenseForm((p) => ({ ...p, expense_type: t.value }));
                        setCommonNewItem((p) => ({ ...p, expense_type: t.value, description: "" }));
                      }}
                      data-testid={`expense-type-${t.value}-btn`}
                      className={`px-4 py-3 rounded-lg border text-left transition-colors ${
                        expenseForm.expense_type === t.value
                          ? t.value === "paiement"
                            ? "bg-rose-500/20 border-rose-500/60 text-rose-100"
                            : "bg-emerald-500/20 border-emerald-500/60 text-emerald-100"
                          : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{t.icon}</span>
                        <span className="font-bold">{t.label}</span>
                      </div>
                      <p className="text-[11px] opacity-75 mt-0.5">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Destination *</Label>
                <Select
                  value={expenseForm.destination || "cuisine"}
                  onValueChange={(v) => {
                    setExpenseForm((p) => ({ ...p, destination: v }));
                    setCommonNewItem((p) => ({ ...p, destination: v }));
                  }}
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="expense-destination-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {DESTINATIONS.map((d) => (
                      <SelectItem key={d.value} value={d.value}>
                        {d.icon} {d.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-slate-300 text-sm">Libellé global (optionnel)</Label>
                <Input
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                  placeholder="Ex : Courses du jeudi"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="common-description-input"
                />
              </div>

              {expenseForm.expense_type !== "paiement" && (
                <div className="bg-emerald-900/15 border border-emerald-500/30 rounded-lg p-3 flex items-start gap-3" data-testid="to-stock-global-toggle-wrap">
                  <button
                    type="button"
                    onClick={() => setExpenseForm((p) => ({ ...p, to_stock: !p.to_stock }))}
                    className={`mt-0.5 w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${expenseForm.to_stock ? "bg-emerald-500" : "bg-slate-600"}`}
                    data-testid="to-stock-global-toggle"
                    aria-pressed={expenseForm.to_stock}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${expenseForm.to_stock ? "translate-x-4" : ""}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-200">
                      Passer en stock {expenseForm.to_stock ? "(activé pour tous les articles)" : "(désactivé par défaut)"}
                    </p>
                    <p className="text-xs text-emerald-100/70 mt-0.5">
                      Si activé, tous les articles iront dans le module Stock (mouvement attendu à l'approbation, validé à la complétion). Vous pouvez ensuite décocher ligne par ligne ci-dessous pour les exclure.
                    </p>
                  </div>
                </div>
              )}

              {/* 🆕 RÉGULARISATION — date rétroactive (Fév 2026) */}
              {expenseForm.expense_type !== "paiement" && (
                <div className="bg-violet-900/20 border border-violet-500/40 rounded-lg p-3" data-testid="regularization-wrap">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => setExpenseForm((p) => ({
                        ...p,
                        is_regularization: !p.is_regularization,
                        // Init date à J-1 quand on active pour la première fois
                        posting_date: !p.is_regularization
                          ? (p.posting_date || new Date(Date.now() - 86400000).toISOString().slice(0, 10))
                          : p.posting_date,
                        regularization_reason: !p.is_regularization ? (p.regularization_reason || "") : p.regularization_reason,
                      }))}
                      className={`mt-0.5 w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${expenseForm.is_regularization ? "bg-violet-500" : "bg-slate-600"}`}
                      data-testid="regularization-toggle"
                      aria-pressed={!!expenseForm.is_regularization}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${expenseForm.is_regularization ? "translate-x-4" : ""}`} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-violet-200 flex items-center gap-1.5">
                        🕒 Régulariser un bon (date rétroactive)
                        {expenseForm.is_regularization && (
                          <span className="text-[10px] bg-violet-500/40 text-violet-100 px-2 py-0.5 rounded-full uppercase tracking-wide">Actif</span>
                        )}
                      </p>
                      <p className="text-xs text-violet-100/70 mt-0.5">
                        Utilise ce mode pour saisir un bon oublié ou un ticket retrouvé à une date passée. La demande passera <strong>quand même</strong> par la validation admin. La date saisie ci-dessous sera utilisée comptablement (statistiques, filtres par mois).
                      </p>
                    </div>
                  </div>

                  {expenseForm.is_regularization && (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-violet-500/20">
                      <div>
                        <label className="text-[11px] uppercase font-bold text-violet-200 tracking-wide">
                          Date comptable rétroactive <span className="text-rose-400">*</span>
                        </label>
                        <Input
                          type="date"
                          value={expenseForm.posting_date || ""}
                          max={new Date().toISOString().slice(0, 10)}
                          onChange={(e) => setExpenseForm((p) => ({ ...p, posting_date: e.target.value }))}
                          className="bg-slate-900 border-violet-500/40 text-white mt-1"
                          data-testid="regularization-posting-date"
                          required
                        />
                        <p className="text-[10px] text-violet-200/60 mt-1">
                          Doit être ≤ aujourd&apos;hui.
                        </p>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase font-bold text-violet-200 tracking-wide">
                          Motif de régularisation <span className="text-rose-400">*</span>
                        </label>
                        <Input
                          type="text"
                          value={expenseForm.regularization_reason || ""}
                          onChange={(e) => setExpenseForm((p) => ({ ...p, regularization_reason: e.target.value }))}
                          placeholder="Ex: Ticket retrouvé, bon oublié en fin de mois…"
                          className="bg-slate-900 border-violet-500/40 text-white mt-1"
                          data-testid="regularization-reason"
                          maxLength={200}
                          required
                        />
                        <p className="text-[10px] text-violet-200/60 mt-1">
                          Obligatoire — sera visible par l&apos;administrateur au moment de la validation.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Card className={`${expenseForm.expense_type === "paiement" ? "bg-rose-900/15 border-rose-500/30" : "bg-purple-900/20 border-purple-500/30"}`}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm flex items-center gap-2 ${expenseForm.expense_type === "paiement" ? "text-rose-200" : "text-purple-300"}`}>
                    <Plus className="w-4 h-4" />
                    {expenseForm.expense_type === "paiement" ? "Ajouter un paiement" : "Ajouter un article"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {expenseForm.expense_type === "paiement" ? (
                    <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                      <Select
                        value={commonNewItem.description || ""}
                        onValueChange={(v) => {
                          const preset = PREDEFINED_PAYMENTS.find((p) => p.label === v);
                          setCommonNewItem({
                            ...commonNewItem,
                            description: v,
                            category: "paiement",
                            expense_type: "paiement",
                            destination: preset?.destination || expenseForm.destination || "administratif",
                          });
                        }}
                      >
                        <SelectTrigger className="flex-1 min-w-[200px] bg-slate-700/50 border-slate-600 text-white" data-testid="payment-preset-select">
                          <SelectValue placeholder="Choisir un type de paiement…" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700 max-h-[300px]">
                          {PAYMENT_GROUPS.map((group) => (
                            <div key={group}>
                              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-900/50">{group}</div>
                              {PREDEFINED_PAYMENTS.filter((p) => p.group === group).map((p) => (
                                <SelectItem key={p.label} value={p.label} className="text-white text-sm">
                                  {p.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        value={commonNewItem.unit_price || ""}
                        onChange={(e) => setCommonNewItem({ ...commonNewItem, quantity: 1, unit_price: parseFloat(e.target.value) || 0 })}
                        placeholder="Montant FCFA"
                        className="w-full sm:w-[140px] bg-slate-700/50 border-slate-600 text-white"
                        data-testid="payment-amount-input"
                      />
                      <Button onClick={addCommonItem} className="bg-rose-600 hover:bg-rose-700" data-testid="common-add-item-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                        <Select value={commonNewItem.category} onValueChange={(v) => setCommonNewItem({ ...commonNewItem, category: v })}>
                          <SelectTrigger className="w-full sm:w-[140px] bg-slate-700/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="cuisine">🍳 Cuisine</SelectItem>
                            <SelectItem value="bar">🍹 Bar</SelectItem>
                            <SelectItem value="paiement">💳 Paiement</SelectItem>
                            <SelectItem value="autres">📦 Autres</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={commonNewItem.description}
                          onChange={(e) => setCommonNewItem({ ...commonNewItem, description: e.target.value })}
                          placeholder="Article / libellé"
                          className="flex-1 min-w-[150px] bg-slate-700/50 border-slate-600 text-white"
                          data-testid="common-new-item-desc"
                        />
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={commonNewItem.quantity === 0 || commonNewItem.quantity === null || commonNewItem.quantity === undefined ? "" : commonNewItem.quantity}
                          onChange={(e) => {
                            const v = e.target.value;
                            setCommonNewItem({ ...commonNewItem, quantity: v === "" ? 0 : parseFloat(v.replace(",", ".")) || 0 });
                          }}
                          placeholder="Qté"
                          className="w-full sm:w-[70px] bg-slate-700/50 border-slate-600 text-white"
                        />
                        <Input
                          type="number"
                          value={commonNewItem.unit_price || ""}
                          onChange={(e) => setCommonNewItem({ ...commonNewItem, unit_price: parseFloat(e.target.value) || 0 })}
                          placeholder="PU"
                          className="w-full sm:w-[100px] bg-slate-700/50 border-slate-600 text-white"
                        />
                        <div className="flex items-center bg-purple-900/30 rounded px-2 text-purple-300 text-sm">
                          = {formatPrice((commonNewItem.quantity || 0) * (commonNewItem.unit_price || 0))} F
                        </div>
                        <Button onClick={addCommonItem} className="bg-purple-600 hover:bg-purple-700" data-testid="common-add-item-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="mt-1">
                        <InlinePriceHint
                          description={commonNewItem.description}
                          unitPrice={commonNewItem.unit_price}
                          supplier={expenseForm.supplier}
                        />
                      </div>
                      <ConditioningSuggester
                        description={commonNewItem.description}
                        category={commonNewItem.category}
                        quantity={commonNewItem.quantity}
                        onApply={(desc, qty) => setCommonNewItem({ ...commonNewItem, description: desc, quantity: qty })}
                        testIdPrefix="common"
                      />
                    </>
                  )}
                </CardContent>
              </Card>

              {commonItems.length > 0 && (
                <Card className="bg-slate-700/30 border-slate-600">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-slate-300 text-sm">Articles ajoutés</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 max-h-[220px] overflow-y-auto">
                    {commonItems.map((it, idx) => {
                      const catLabel =
                        { cuisine: "🍳 Cuisine", bar: "🍹 Bar", paiement: "💳 Paiement", autres: "📦 Autres" }[it.category] || it.category;
                      const itemToStock =
                        it.passer_en_stock === null || it.passer_en_stock === undefined ? !!expenseForm.to_stock : !!it.passer_en_stock;
                      const isPaiement = it.expense_type === "paiement" || it.category === "paiement";
                      return (
                        <div key={it.id} className="flex items-center justify-between gap-2 bg-slate-600/30 rounded-lg p-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className="text-slate-400 text-sm font-mono">{idx + 1}.</span>
                            <Badge className="text-xs shrink-0 bg-slate-700/50 text-slate-300">{catLabel}</Badge>
                            <span className="text-white truncate">{it.description}</span>
                            {!isPaiement && (
                              <button
                                type="button"
                                onClick={() => {
                                  setCommonItems(
                                    commonItems.map((ci) => (ci.id === it.id ? { ...ci, passer_en_stock: !itemToStock } : ci))
                                  );
                                }}
                                className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full font-semibold transition flex-shrink-0 ${
                                  itemToStock
                                    ? "bg-emerald-500/30 text-emerald-200 hover:bg-emerald-500/40"
                                    : "bg-slate-700/40 text-slate-400 hover:bg-slate-700/60"
                                }`}
                                title={
                                  itemToStock
                                    ? "Cet article ira en stock — cliquez pour désactiver"
                                    : "Cet article n'ira PAS en stock — cliquez pour activer"
                                }
                                data-testid={`item-stock-toggle-${it.id}`}
                              >
                                {itemToStock ? "→ stock" : "hors stock"}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-slate-400 text-xs">
                              {it.quantity} × {formatPrice(it.unit_price)} = {formatPrice(it.amount)} F
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeCommonItem(it.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-7 w-7 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="flex justify-end pt-1 border-t border-slate-600 mt-2">
                      <span className="text-purple-300 font-bold">Total : {formatPrice(getCommonTotal())} F</span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <div>
            <Label className="text-slate-300">Fournisseur</Label>
            <Input
              value={expenseForm.supplier}
              onChange={(e) => setExpenseForm({ ...expenseForm, supplier: e.target.value })}
              placeholder="Nom du fournisseur"
              className="bg-slate-700/50 border-slate-600 text-white"
            />
          </div>

          <div>
            <Label className="text-slate-300">Date prévue d'achat</Label>
            <Input
              type="date"
              value={expenseForm.planned_date}
              onChange={(e) => setExpenseForm({ ...expenseForm, planned_date: e.target.value })}
              className="bg-slate-700/50 border-slate-600 text-white"
            />
          </div>

          <div className="bg-cyan-900/20 border border-cyan-500/30 rounded p-3 space-y-2">
            <Label className="text-cyan-300 text-sm flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Payé depuis (source de financement)
            </Label>
            <select
              value={expenseForm.funded_by_account_id || ""}
              onChange={(e) => {
                const id = e.target.value;
                const acc = availableAccounts.find((a) => a.id === id);
                setExpenseForm({
                  ...expenseForm,
                  funded_by_account_id: id,
                  funded_by_account_name: acc?.name || "",
                });
              }}
              className="w-full bg-slate-700/50 border border-slate-600 text-white rounded px-3 py-2 text-sm"
              data-testid="expense-funded-by-select"
            >
              <option value="">💰 Recettes de la caisse (par défaut)</option>
              {availableAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  📒 {acc.name} — Dispo : {formatPrice(acc.balance_available || 0)} F
                </option>
              ))}
            </select>
            {expenseForm.funded_by_account_id && (
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!expenseForm.funded_affects_ca}
                  onChange={(e) => setExpenseForm({ ...expenseForm, funded_affects_ca: e.target.checked })}
                  className="w-3.5 h-3.5 accent-cyan-400"
                  data-testid="expense-funded-affects-ca"
                />
                Cette dépense est quand même déduite du CA journalier
              </label>
            )}
          </div>

          <div>
            <Label className="text-slate-300">Photo du reçu/bon (optionnel)</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setExpenseForm({ ...expenseForm, receipt_image: reader.result });
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="bg-slate-700/50 border-slate-600 text-white file:bg-purple-600 file:text-white file:border-0 file:rounded file:mr-2"
            />
            {expenseForm.receipt_image && (
              <div className="mt-2 relative">
                <img src={expenseForm.receipt_image} alt="Reçu" className="w-full max-h-32 object-cover rounded border border-slate-600" />
                <Button
                  size="sm"
                  variant="destructive"
                  className="absolute top-1 right-1"
                  onClick={() => setExpenseForm({ ...expenseForm, receipt_image: null })}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={() => {
                if (editingExpense) {
                  updateExpense(editingExpense.id, {
                    ...expenseForm,
                    status: "pending",
                  });
                  onOpenChange(false);
                } else {
                  createExpense();
                }
              }}
              disabled={expenseSubmitLoading}
              className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-wait"
              data-testid="save-common-expense-btn"
            >
              {expenseSubmitLoading
                ? "Envoi en cours..."
                : editingExpense
                ? "Soumettre à nouveau"
                : `Soumettre ${commonItems.length > 0 ? `${commonItems.length} article(s)` : "la demande"}`}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-slate-600 text-slate-400"
            >
              Annuler
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExpenseModal;
